import { inputWord } from "@workforceos/shared";
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, ApiError, apiErrorMessage } from "../../api/client";
import { validatorLabel } from "../../lib/validatorLabel";
import { formatWhen, readableValue } from "../../lib/when";
import { roleLabel } from "../../lib/roleLabel";
import { usePersona } from "../../hooks/usePersona";

/**
 * Judgement forks — the queue of moments the workforce could not finish
 * alone, and the one place a person touches a run.
 *
 * It deliberately does NOT show the work: a reviewer opening this page should
 * see the decisions waiting on them and the reason for each, not a pack to
 * read. The reason comes first because it is what determines who can act — a
 * statutory block needs a compliance reviewer, a missing input needs whoever
 * owns the data. The server enforces that; this page says so when it refuses.
 *
 * A decision is a record. It calls no model and cannot be undone.
 */

interface Fork {
  run_id: string;
  hand_key: string;
  hand_name: string;
  verdict: string;
  created_at: string;
  blocking_rules: {
    rule_id: string;
    summary: string;
    origin: string;
    origin_ref: string | null;
    citation: string;
  }[];
  missing: string[];
  /** Optional so a page served from cache does not blank on an older API. */
  missing_rules?: {
    rule_id: string;
    summary: string | null;
    origin: string | null;
    origin_ref: string | null;
    citation: string | null;
  }[];
  /** Which record this run judged — the campaign, the case, the day. */
  subject: { label: string; value: string } | null;
  /** What kind of decision this is, and whose signature releases it. */
  decision?: {
    key: string;
    label: string;
    stage: string;
    approver_role: string;
  } | null;
  failed_validators: string[];
  model_called: boolean;
  resolution: {
    decision: string;
    reason: string;
    resolved_role: string;
    resolved_at: string;
  } | null;
}

const DECISIONS = [
  { value: "approved", label: "Approve" },
  { value: "rejected", label: "Reject" },
  { value: "needs_more_info", label: "Ask for more" },
] as const;

/**
 * No decision is preselected. The control opened on "Reject", one click from
 * recording a rejection nobody chose — and a decision cannot be undone.
 */
const UNCHOSEN = "";

function originLabel(origin: string, ref: string | null): string {
  if (origin === "statutory") return ref ? `Statute · ${ref}` : "Statute";
  if (origin === "regulator_rule") return ref ? `Regulator · ${ref}` : "Regulator rule";
  if (origin === "sharia_ruling") return ref ? `Sharia ruling · ${ref}` : "Sharia ruling";
  if (origin === "tenant_policy") return ref ? `Client policy · ${ref}` : "Client policy";
  return "Placeholder — not policy, not statute";
}

export function decisionLabel(d: string): string {
  return DECISIONS.find((x) => x.value === d)?.label ?? d;
}

/**
 * The one line that tells a person why they are being asked — in words.
 *
 * "Waiting on: PRM-004, PRM-007" is what the run record says. It is not
 * something a compliance reviewer can act on, and two campaigns held for the
 * same rule read identically. The rule's own sentence goes here; the codes
 * and citations sit underneath.
 */
function reason(f: Fork): string {
  if (f.blocking_rules.length > 0) return f.blocking_rules[0]!.summary;
  const named = (f.missing_rules ?? []).find((m) => m.summary);
  if (named) return named.summary!;
  if (f.missing.length > 0) return `Waiting on: ${[...new Set(f.missing.map((m) => inputWord(m, "en")))].join("; ")}`;
  if (f.failed_validators.length > 0) return validatorLabel(f.failed_validators[0]!);
  return "Needs a decision";
}

/**
 * True when this fork is an obligation's to close.
 *
 * The server refuses anyone but a compliance reviewer (403), and the page used
 * to find that out only after a decision had been typed and submitted. The
 * same origins the server checks are on the card already, so say it first.
 */
const OBLIGATION = new Set(["statutory", "regulator_rule", "sharia_ruling"]);

function needsReviewer(f: Fork): boolean {
  return (
    f.blocking_rules.some((r) => OBLIGATION.has(r.origin)) ||
    (f.missing_rules ?? []).some((r) => r.origin !== null && OBLIGATION.has(r.origin))
  );
}

/**
 * The role whose signature releases this, and whether it is the reader's.
 *
 * An obligation always outranks the decision's own approver: a statutory
 * block is a compliance reviewer's whoever the pack is addressed to.
 */
function approverRole(f: Fork): string {
  if (needsReviewer(f)) return "reviewer";
  return f.decision?.approver_role ?? "reviewer";
}

function mine(f: Fork, persona: string | null): boolean {
  return persona !== null && persona === approverRole(f);
}

/** "and 2 more" — said once, rather than repeating every sentence. */
function alsoWaiting(f: Fork): string | null {
  const rest = f.blocking_rules.length > 0 ? f.blocking_rules.length - 1 : f.missing.length - 1;
  return rest > 0 ? `and ${rest} more on this run` : null;
}

function ResolvePanel({ fork, workspaceId }: { fork: Fork; workspaceId: string }) {
  const qc = useQueryClient();
  const [decision, setDecision] = useState<string>(UNCHOSEN);
  const [why, setWhy] = useState("");
  // Step-up (NEW-CS-5): set once the server asks for a code; the code is sent then.
  const [stepUp, setStepUp] = useState(false);
  const [code, setCode] = useState("");
  const approving = decision === "approved";
  const sendCode = useMutation({
    mutationFn: () => apiPost<{ sent: boolean; expires_in_s: number; demo_code?: string }>(`/api/workspaces/${workspaceId}/forks/${fork.run_id}/step-up`, {}),
  });

  const resolve = useMutation({
    mutationFn: () =>
      apiPost<{ decision: string }>(
        `/api/workspaces/${workspaceId}/forks/${fork.run_id}/resolve`,
        { decision, reason: why, ...(stepUp && approving ? { code } : {}) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["forks", workspaceId] });
    },
    onError: (e) => {
      const err = e instanceof ApiError ? (e.body as { error?: string } | null)?.error : undefined;
      if (err === "step_up_required" && !stepUp) {
        setStepUp(true);
        sendCode.mutate();
      }
    },
  });

  const askedForCode =
    resolve.error instanceof ApiError && (resolve.error.body as { error?: string } | null)?.error === "step_up_required";
  const refusal =
    resolve.isError && !askedForCode
      ? apiErrorMessage(resolve.error)
      : sendCode.isError
        ? apiErrorMessage(sendCode.error)
        : null;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="card-section"
      data-testid={`resolve-${fork.run_id}`}
    >
      <div className="section-eyebrow">YOUR DECISION</div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap", marginTop: 6 }}>
        <select
          aria-label="Decision"
          value={decision}
          onChange={(e) => setDecision(e.target.value)}
        >
          <option value={UNCHOSEN}>Choose a decision…</option>
          {DECISIONS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
        <textarea
          aria-label="Reason"
          placeholder="Why — for the audit trail and the next reviewer. At least a sentence."
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          rows={2} style={{ flex: "1 1 260px", minWidth: 220 }}
        />
        <button
          type="button"
          className="btn primary"
          data-testid={`resolve-submit-${fork.run_id}`}
          disabled={resolve.isPending || decision === UNCHOSEN || why.trim().length < 10 || (stepUp && approving && code.length !== 6)}
          onClick={() => resolve.mutate()}
        >
          {resolve.isPending ? "Recording…" : "Record decision"}
        </button>
      </div>
      {stepUp && approving ? (
        <div className="play-meta" style={{ marginTop: 6, gap: 8, flexWrap: "wrap" }}>
          {sendCode.isPending ? <span>Sending a code…</span> : null}
          {sendCode.data?.sent ? <span>We sent a code to your email. Enter it to approve.</span> : null}
          {sendCode.data?.demo_code ? <span>Demo: your code is {sendCode.data.demo_code}</span> : null}
          <input
            aria-label="Six-digit code"
            data-testid={`resolve-code-${fork.run_id}`}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            style={{ width: "9ch", letterSpacing: "0.3em" }}
          />
          <button type="button" className="btn" disabled={sendCode.isPending} onClick={() => sendCode.mutate()}>
            Send again
          </button>
        </div>
      ) : null}
      {refusal ? (
        <div className="play-meta" style={{ marginTop: 6 }} data-testid={`resolve-refused-${fork.run_id}`}>
          <span className="pill red">{refusal}</span>
        </div>
      ) : null}
    </div>
  );
}

function ForkCard({ fork, workspaceId }: { fork: Fork; workspaceId: string }) {
  const navigate = useNavigate();
  const persona = usePersona(workspaceId);
  const decided = fork.resolution;
  return (
    <div
      className="play-card"
      data-testid={`fork-${fork.run_id}`}
      style={{ cursor: "pointer" }}
      onClick={() => navigate(`/workspaces/${workspaceId}/workforce/runs/${fork.run_id}`)}
    >
      <div className="play-head">
        <div>
          <div className="play-arch">
            {fork.hand_name}
            {fork.subject ? (
              <>
                {" · "}
                <span data-testid={`fork-subject-${fork.run_id}`}>
                  {fork.subject.label} {readableValue(fork.subject.value)}
                </span>
              </>
            ) : null}
          </div>
          <div className="play-title">{reason(fork)}</div>
          {alsoWaiting(fork) ? (
            <div className="play-arch" style={{ marginTop: 2 }}>{alsoWaiting(fork)}</div>
          ) : null}
          {fork.decision ? (
            <div className="play-arch" style={{ marginTop: 2 }} data-testid={`fork-decision-${fork.run_id}`}>
              {fork.decision.label} · signed off by {roleLabel(approverRole(fork))}
            </div>
          ) : null}
        </div>
        <div className="play-status">
          {decided ? (
            <span className={`pill ${decided.decision === "approved" ? "green" : decided.decision === "rejected" ? "red" : "amber"}`}>
              {decisionLabel(decided.decision).toLowerCase()}
            </span>
          ) : (
            <span className={`pill ${fork.blocking_rules.length > 0 ? "red" : "amber"}`}>
              {fork.blocking_rules.length > 0 ? "blocked by a rule" : "needs input"}
            </span>
          )}
        </div>
      </div>

      {fork.blocking_rules.length > 0 ? (
        <div className="play-meta">
          {fork.blocking_rules.map((r) => (
            <span key={r.rule_id}>
              {r.rule_id} <span className="v">{originLabel(r.origin, r.origin_ref)}</span>
            </span>
          ))}
        </div>
      ) : (fork.missing_rules ?? []).length > 0 ? (
        <div className="play-meta">
          {(fork.missing_rules ?? []).map((r) => (
            <span key={r.rule_id}>
              {r.rule_id}{" "}
              <span className="v">
                {r.origin ? originLabel(r.origin, r.origin_ref) : "not in the pack"}
              </span>
            </span>
          ))}
        </div>
      ) : null}

      <div className="play-foot">
        <span>{formatWhen(fork.created_at)}</span>
        {/* Worth saying: the agent stopped before spending anything. */}
        <span>{fork.model_called ? "drafted, then held" : "stopped before drafting"}</span>
        {/* The card is clickable, but nothing on it said so. */}
        <span className="v" data-testid={`fork-open-${fork.run_id}`}>Open the run →</span>
      </div>

      {!decided && !mine(fork, persona) ? (
        <div
          className="play-meta"
          style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 10 }}
          data-testid={`reviewer-only-${fork.run_id}`}
        >
          <span>
            {needsReviewer(fork)
              ? "An obligation raised this fork, so only a compliance reviewer can decide it. It stays here until one does."
              : `This is released by ${roleLabel(approverRole(fork))}, and stays here until they sign it.`}
          </span>
        </div>
      ) : null}

      {decided ? (
        <div className="play-meta" style={{ marginTop: 8 }} data-testid={`decided-${fork.run_id}`}>
          <span>
            {decisionLabel(decided.decision)} by {roleLabel(decided.resolved_role)} on{" "}
            {formatWhen(decided.resolved_at)}
          </span>
          <span style={{ opacity: 0.8 }}>“{decided.reason}”</span>
        </div>
      ) : !mine(fork, persona) ? null : (
        <ResolvePanel fork={fork} workspaceId={workspaceId} />
      )}
    </div>
  );
}

function ForksInner() {
  const { workspaceId = "" } = useParams();
  const [showDecided, setShowDecided] = useState(false);
  const q = useQuery<{ forks: Fork[] }, Error>({
    queryKey: ["forks", workspaceId, showDecided],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<{ forks: Fork[] }>(
        `/api/workspaces/${workspaceId}/forks${showDecided ? "?include=resolved" : ""}`,
      ),
  });

  const persona = usePersona(workspaceId);
  const all = q.data?.forks ?? [];
  const everyOpen = all.filter((f) => !f.resolution);
  // Yours first; the rest is visible but apart, and the header counts only
  // yours — the same number the app shows (usability review).
  const open = everyOpen.filter((f) => mine(f, persona));
  const others = everyOpen.filter((f) => !mine(f, persona));
  const decided = all.filter((f) => f.resolution);
  const byRule = open.filter((f) => f.blocking_rules.length > 0).length;

  return (
    <div className="main-inner" data-testid="forks">
      <div className="greet-row">
        <span className="greet">Decisions</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">WAITING ON PEOPLE</div>
        <div className="lede">
          {open.length === 0
            ? "Nothing is waiting on you."
            : `${open.length} ${open.length === 1 ? "decision" : "decisions"} waiting on you${
                byRule > 0 ? `, ${byRule} blocked by a rule with a citation` : ""
              }.`}{" "}
          <button
            type="button"
            className="btn"
            data-testid="toggle-decided"
            onClick={() => setShowDecided((v) => !v)}
          >
            {showDecided ? "Hide decided" : "Show decided"}
          </button>
        </div>
      </div>

      <section data-testid="forks-mine">
      <div className="section-h">
        <span className="ttl">Waiting on you ({open.length})</span>
      </div>
      <div className="play-grid">
        {q.isLoading ? (
          <div className="play-card">
            <div className="play-title">Loading…</div>
          </div>
        ) : open.length === 0 ? (
          <div className="play-card" data-testid="forks-empty">
            <div className="play-title">Nothing needs you right now.</div>
            <div className="play-meta">
              <span>
                Agents run on their schedules. Anything they cannot finish alone
                arrives here, with the reason.
              </span>
            </div>
          </div>
        ) : (
          open.map((f) => <ForkCard key={f.run_id} fork={f} workspaceId={workspaceId} />)
        )}
      </div>
      </section>

      {others.length > 0 ? (
        <section data-testid="forks-others">
          <div className="section-h">
            <span className="ttl">Waiting on others ({others.length})</span>
          </div>
          <div className="play-grid">
            {others.map((f) => (
              <ForkCard key={f.run_id} fork={f} workspaceId={workspaceId} />
            ))}
          </div>
        </section>
      ) : null}

      {showDecided && decided.length > 0 ? (
        <>
          <div className="section-h">
            <span className="ttl">Decided</span>
          </div>
          <div className="play-grid">
            {decided.map((f) => (
              <ForkCard key={f.run_id} fork={f} workspaceId={workspaceId} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

export function Forks() {
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <div className="main-inner" data-testid="forks-error">
          <div className="lede">Could not load the queue: {error.message}</div>
        </div>
      )}
    >
      <ForksInner />
    </ErrorBoundary>
  );
}
